import { useState, useEffect, useCallback } from 'react';
import { Search, Filter, Loader2, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PlazaNoteCard } from '@/components/notes/PlazaNoteCard';
import { plazaApi, airportApi } from '@/services/notesApi';
import { useToast } from '@/components/ui/use-toast';
import type { PlazaNoteListItem, AirportSimple } from '@/types/notes.types';

type SortBy = 'latest' | 'mostLiked' | 'mostFavorited';

const SORT_OPTIONS: { key: SortBy; label: string }[] = [
  { key: 'latest', label: '最新' },
  { key: 'mostLiked', label: '最热点赞' },
  { key: 'mostFavorited', label: '最多收藏' },
];

export default function NotesPlazaPage() {
  const { toast } = useToast();
  const [notes, setNotes] = useState<PlazaNoteListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('latest');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // 筛选状态
  const [airports, setAirports] = useState<AirportSimple[]>([]);
  const [selectedAirport, setSelectedAirport] = useState('');
  const [departure, setDeparture] = useState('');
  const [arrival, setArrival] = useState('');

  // 加载机场列表
  useEffect(() => {
    airportApi.getAirports({ simple: true, pageSize: 500 }).then((res) => {
      setAirports(res.data.results || []);
    });
  }, []);

  const fetchNotes = useCallback(async (pageNum = 1, append = false) => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page: pageNum, pageSize: 20, sortBy };
      if (search) params.search = search;
      if (selectedAirport) params.airport = selectedAirport;
      if (departure) params.departure = departure;
      if (arrival) params.arrival = arrival;

      const res = await plazaApi.getPlazaNotes(params as any);
      const results = res.data.results || [];
      const count = res.data.count || 0;

      if (append) {
        setNotes(prev => [...prev, ...results]);
      } else {
        setNotes(results);
      }
      setHasMore(results.length === 20 && (append ? notes.length + results.length : results.length) < count);
    } catch {
      toast({ title: '获取广场笔记失败', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy, selectedAirport, departure, arrival, toast]);

  useEffect(() => {
    setPage(1);
    fetchNotes(1, false);
  }, [fetchNotes]);

  const handleSearch = () => {
    setSearch(searchInput);
  };

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchNotes(next, true);
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* 页面标题 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Globe className="w-6 h-6 text-blue-500" />
          笔记广场
        </h1>
        <p className="text-sm text-gray-500 mt-1">浏览其他飞行员分享的经验笔记</p>
      </div>

      {/* 筛选栏 */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 space-y-3">
        {/* 搜索 */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="搜索笔记标题、内容..."
              className="pl-9"
            />
          </div>
          <Button variant="outline" onClick={handleSearch}>搜索</Button>
        </div>

        {/* 机场/航线筛选 */}
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={selectedAirport}
            onChange={(e) => { setSelectedAirport(e.target.value); setDeparture(''); setArrival(''); }}
            className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">按机场筛选</option>
            {airports.map(ap => (
              <option key={ap.iataCode} value={ap.iataCode}>
                {ap.iataCode} {ap.nameZh}
              </option>
            ))}
          </select>

          <span className="text-xs text-gray-400">或</span>

          <select
            value={departure}
            onChange={(e) => { setDeparture(e.target.value); setSelectedAirport(''); }}
            className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">出发机场</option>
            {airports.map(ap => (
              <option key={ap.iataCode} value={ap.iataCode}>
                {ap.iataCode} {ap.nameZh}
              </option>
            ))}
          </select>

          <select
            value={arrival}
            onChange={(e) => { setArrival(e.target.value); setSelectedAirport(''); }}
            className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">到达机场</option>
            {airports.map(ap => (
              <option key={ap.iataCode} value={ap.iataCode}>
                {ap.iataCode} {ap.nameZh}
              </option>
            ))}
          </select>
        </div>

        {/* 排序 */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">排序:</span>
          {SORT_OPTIONS.map(opt => (
            <button
              key={opt.key}
              onClick={() => setSortBy(opt.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                sortBy === opt.key
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* 笔记列表 */}
      {loading && notes.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : notes.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
            <Filter className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">暂无分享笔记</h3>
          <p className="text-sm text-gray-500">当前筛选条件下没有找到已分享的笔记</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {notes.map(note => (
            <PlazaNoteCard key={note.id} note={note} />
          ))}
        </div>
      )}

      {/* 加载更多 */}
      {hasMore && (
        <div className="mt-6 text-center">
          <Button variant="outline" onClick={handleLoadMore} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            加载更多
          </Button>
        </div>
      )}
    </div>
  );
}
